import React, { useLayoutEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Space, Typography } from 'antd';
import type { AiRunPayloadSelection } from '../aiRunPayloadSelection';
import { isAiRunPayloadSelectionContinueDisabled, labelAiRunTreeScenario } from '../aiRunPayloadSelection';

export type DashboardAiRunPayloadFormProps = {
    theme: 'dark' | 'light';
    scenarios: any[];
    showIntro?: boolean;
    cancelLabel?: string;
    continueLabel?: string;
    onCancel?: () => void;
    onContinue: (selection: AiRunPayloadSelection) => void;
    continueDisabled?: boolean;
    /** When false, only Continue is shown (e.g. modal still has Modal-level cancel). */
    showCancelButton?: boolean;
    /** When false, footer buttons are omitted (actions live next to the error message). */
    showFooterActions?: boolean;
    onSelectionChange?: (selection: AiRunPayloadSelection) => void;
};

function initialScenarioChecked(scenarios: any[]): Record<number, boolean> {
    const next: Record<number, boolean> = {};
    scenarios.forEach((_, i) => {
        next[i] = true;
    });
    return next;
}

export function DashboardAiRunPayloadForm(props: DashboardAiRunPayloadFormProps) {
    const {
        theme,
        scenarios,
        showIntro = true,
        cancelLabel = 'Cancel',
        continueLabel = 'Continue',
        onCancel,
        onContinue,
        continueDisabled: continueDisabledProp,
        showCancelButton = true,
        showFooterActions = true,
        onSelectionChange,
    } = props;

    const [scenarioChecked, setScenarioChecked] = useState<Record<number, boolean>>(() =>
        initialScenarioChecked(scenarios),
    );
    const [includeGlobalAllRoofs, setIncludeGlobalAllRoofs] = useState(true);
    const [includeGlobalBestSubsets, setIncludeGlobalBestSubsets] = useState(true);
    const [includeCategoryLayoutCards, setIncludeCategoryLayoutCards] = useState(true);

    const toggleScenario = (index: number, checked: boolean) => {
        if (index === 0) return;
        setScenarioChecked((prev) => ({ ...prev, [index]: checked }));
    };

    const selectAllTreeScenarios = () => {
        setScenarioChecked((prev) => {
            const next = { ...prev };
            scenarios.forEach((_, i) => {
                next[i] = true;
            });
            return next;
        });
    };

    const clearOptionalTreeScenarios = () => {
        setScenarioChecked((prev) => {
            const next = { ...prev };
            scenarios.forEach((_, i) => {
                if (i !== 0) next[i] = false;
            });
            return next;
        });
    };

    const selection = useMemo((): AiRunPayloadSelection => {
        const indices = scenarios
            .map((_, i) => i)
            .filter((i) => i === 0 || scenarioChecked[i] !== false);
        return {
            scenarioIndices: indices.length ? indices : [0],
            includeGlobalAllRoofs,
            includeGlobalBestSubsets,
            includeCategoryLayoutCards,
        };
    }, [scenarios, scenarioChecked, includeGlobalAllRoofs, includeGlobalBestSubsets, includeCategoryLayoutCards]);

    useLayoutEffect(() => {
        onSelectionChange?.(selection);
    }, [selection, onSelectionChange]);

    const handleContinue = () => {
        onContinue(selection);
    };

    const layoutDisabled = isAiRunPayloadSelectionContinueDisabled(selection);
    const continueDisabled = continueDisabledProp === true || layoutDisabled;

    return (
        <div className={`obd-ai-run-payload-form obd-theme-${theme}`}>
            <div className="obd-ai-run-payload-form__scroll">
            {showIntro ? (
                <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
                    Uncheck items you don&apos;t need to shrink the AI request (helps avoid input size limits).
                    Baseline tree-cutting data is always included.
                </Typography.Paragraph>
            ) : null}

            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 12,
                    marginBottom: 8,
                }}
            >
                <Typography.Title level={5} style={{ margin: 0, flex: '0 0 auto' }}>
                    Tree-cutting scenarios
                </Typography.Title>
                <Space wrap size="small" style={{ flex: '0 0 auto' }}>
                    <Button size="small" type="link" onClick={selectAllTreeScenarios} style={{ padding: 0 }}>
                        Select all
                    </Button>
                    <Button size="small" type="link" onClick={clearOptionalTreeScenarios} style={{ padding: 0 }}>
                        Clear
                    </Button>
                </Space>
            </div>
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    gap: '6px 20px',
                    border: theme === 'dark' ? '1px solid rgba(148,163,184,0.25)' : '1px solid #e2e8f0',
                    borderRadius: 8,
                    padding: '10px 12px',
                    marginBottom: 20,
                    maxHeight: 220,
                    overflowY: 'auto',
                }}
            >
                {scenarios.map((s, index) => (
                    <div
                        key={index}
                        style={{
                            flex: '0 1 auto',
                            minWidth: 0,
                            maxWidth: 'min(100%, 280px)',
                        }}
                    >
                        <Checkbox
                            checked={index === 0 ? true : scenarioChecked[index] !== false}
                            disabled={index === 0}
                            onChange={(e) => toggleScenario(index, e.target.checked)}
                            style={{ alignItems: 'flex-start' }}
                        >
                            {labelAiRunTreeScenario(s, index)}
                        </Checkbox>
                    </div>
                ))}
            </div>

            <Typography.Title level={5}>Layout simulations (for the AI prompt)</Typography.Title>
            <Space direction="vertical" style={{ width: '100%' }}>
                <Checkbox checked={includeGlobalAllRoofs} onChange={(e) => setIncludeGlobalAllRoofs(e.target.checked)}>
                    All roofs — portrait / landscape / mixed / balanced efficiency trials
                </Checkbox>
                <Checkbox
                    checked={includeGlobalBestSubsets}
                    onChange={(e) => setIncludeGlobalBestSubsets(e.target.checked)}
                >
                    Top-producing roofs — orientation trials on roof subsets
                </Checkbox>
                <Checkbox
                    checked={includeCategoryLayoutCards}
                    onChange={(e) => setIncludeCategoryLayoutCards(e.target.checked)}
                >
                    Category cards (Max production, Best access, Balanced, Efficiency, etc.)
                </Checkbox>
            </Space>
            {layoutDisabled ? (
                <Typography.Paragraph type="danger" style={{ marginTop: 12, marginBottom: 0 }}>
                    Select at least one layout group to continue.
                </Typography.Paragraph>
            ) : null}
            </div>

            {showFooterActions ? (
                <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: 20, flexShrink: 0 }}>
                    {showCancelButton && onCancel ? (
                        <Button onClick={onCancel}>{cancelLabel}</Button>
                    ) : null}
                    <Button type="primary" onClick={handleContinue} disabled={continueDisabled}>
                        {continueLabel}
                    </Button>
                </Space>
            ) : null}
        </div>
    );
}
